import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export type AuthDialogMode = 'login' | 'register' | 'forgot';

interface AuthDialogValue {
  open: boolean;
  mode: AuthDialogMode;
  openAuth: (mode?: AuthDialogMode) => void;
  closeAuth: () => void;
  finishSignedIn: () => void;
  setMode: (mode: AuthDialogMode) => void;
}

const AuthDialogContext = createContext<AuthDialogValue | null>(null);

function isAuthRoute(pathname: string): boolean {
  return pathname === '/auth/login' || pathname === '/auth/register';
}

export function AuthDialogProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [mode, setModeState] = useState<AuthDialogMode>('login');

  const syncRoute = useCallback(
    (next: AuthDialogMode) => {
      if (!isAuthRoute(location.pathname)) return;
      if (next === 'register' && location.pathname !== '/auth/register') {
        navigate('/auth/register', { replace: true });
      } else if ((next === 'login' || next === 'forgot') && location.pathname === '/auth/register') {
        navigate('/auth/login', { replace: true });
      }
    },
    [location.pathname, navigate],
  );

  const openAuth = useCallback((next: AuthDialogMode = 'login') => {
    setModeState(next);
    setOpen(true);
  }, []);

  const closeAuth = useCallback(() => {
    setOpen(false);
    if (isAuthRoute(location.pathname) || location.pathname === '/auth/forgot') {
      navigate('/', { replace: true });
    }
  }, [location.pathname, navigate]);

  const finishSignedIn = useCallback(() => {
    setOpen(false);
    if (location.pathname !== '/') {
      navigate('/', { replace: true });
    }
  }, [location.pathname, navigate]);

  const setMode = useCallback(
    (next: AuthDialogMode) => {
      setModeState(next);
      setOpen(true);
      syncRoute(next);
    },
    [syncRoute],
  );

  const value = useMemo(
    () => ({ open, mode, openAuth, closeAuth, finishSignedIn, setMode }),
    [open, mode, openAuth, closeAuth, finishSignedIn, setMode],
  );

  return <AuthDialogContext.Provider value={value}>{children}</AuthDialogContext.Provider>;
}

export function useAuthDialog(): AuthDialogValue {
  const ctx = useContext(AuthDialogContext);
  if (!ctx) {
    throw new Error('useAuthDialog must be used within AuthDialogProvider');
  }
  return ctx;
}
